import { Component, OnInit, ElementRef, ViewChild, Input, EventEmitter, Output, HostListener } from '@angular/core';
import { ENTER } from '@angular/cdk/keycodes';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocomplete } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Grilla } from '../grilla';
import { Tabla } from '../tabla';
import { HojeadorComponent } from '../hojeador';
import { FocusMonitor } from '@angular/cdk/a11y';

export interface ChipOption {
	label: string;
	campo: string;
	simple?: boolean; // indica si el chip es simple sin entrada de value (ej. Activos; o Sin IVA; etc.)
	value?: string; // valor que no viene en Input() chips
}

export interface ChipSelected {
	campo: string;
	value?: string;
}

@Component({
// tslint:disable-next-line: component-selector
  selector: 'tr2-busca-chips',
  templateUrl: './busca-chips.component.html',
  styleUrls: ['./busca-chips.component.scss']
})
export class BuscaChips {

	visible = true;
	selectable = true;
	removable = true;
	addOnBlur = true;
	focused = false;
	separatorKeysCodes: number[] = [ENTER];
	searchCtrl = new FormControl();
	chipsFiltrados: Observable<ChipOption[]>;
	chipOptionsSelected: ChipOption[] = [];
	@Input() chips: ChipOption[] = [];
	@Input() placeholder = 'Búsqueda';
	@Input() clase = 'w-100';

	@Input()
	set grilla(component: Grilla) {
		this.setChipsOnChange(component);
	}

	@Input()
	set tabla(component: Tabla) {
		this.setChipsOnChange(component);
	}

	@ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;
	@ViewChild('auto', { static: true }) matAutocomplete: MatAutocomplete;
	typing = false;

	@Output() chipsChange: EventEmitter<ChipSelected[]> = new EventEmitter();

	constructor(
		private fm: FocusMonitor,
		private elRef: ElementRef<HTMLElement>
	) {
		this.chipsFiltrados = this.searchCtrl.valueChanges.pipe(
			startWith(null),
			map((search: string | null) => {
				return this._filter(search);
			}));
		fm.monitor(elRef, true).subscribe(origin => {
			this.focused = !!origin;
		});
	}

	setChipsOnChange(component: any) {
		const sub = this.chipsChange.subscribe((chips: ChipSelected[]) => {
			if (component instanceof Grilla) {
				component.trgrillatabla.setChips(chips);
			} else if (component instanceof Tabla) {
				component.setChips(chips);
			} else {
				sub.unsubscribe();
			}
		});
	}

	get chipsValues(): ChipSelected[] {
		return this.chipOptionsSelected
			.filter(c => c.value || !!c.simple)
			.map((chip: ChipOption) => {
				return {
					campo: chip.campo ? chip.campo : null,
					value: chip.value
				};
			});
	}

	add(event: MatChipInputEvent): void {
		if (!this.matAutocomplete.isOpen) {
			const input = event.input;
			const value = event.value;

			if ((value || '').trim()) {
				if (this.typing && this.chipOptionsSelected.length === 0) {
					this.typing = false;
					return;
				}
				if (this.typing) {
					this.chipOptionsSelected[this.chipOptionsSelected.length - 1].value = value.trim();
					this.typing = false;
				} else {
					this.chipOptionsSelected.push({
						label: null,
						value: value.trim(),
						campo: null
					});
				}
				this.chipsChange.emit(this.chipsValues);
			}

			if (input) {
				input.value = '';
			}

			this.searchCtrl.setValue(null);
		}
	}

	remove(chip: ChipOption): void {
		const index = this.chipOptionsSelected.indexOf(chip);
		if (index >= 0) {
			this.chipOptionsSelected.splice(index, 1);
			this.chipsChange.emit(this.chipsValues);
		}
	}

	selected(event: MatAutocompleteSelectedEvent): void {
		const chip: ChipOption = event.option.value;
		if (chip) {
			this.chipOptionsSelected.push(Object.assign({}, chip));
			this.typing = true;
			this.input.nativeElement.value = '';
			this.searchCtrl.setValue(null);
			if (!!chip.simple) {
				this.chipsChange.emit(this.chipsValues);
			}
		}
	}

	clearChips() {
		this.input.nativeElement.value = '';
		this.chipOptionsSelected = [];
		this.typing = false;
		this.searchCtrl.setValue(null);
		this.chipsChange.emit(this.chipsValues);
	}

	private _filter(value: string): ChipOption[] {
		if (!this.typing && value && typeof value === 'string' && value.substring(0, 1) === '.') {
			const filterValue = value.toLowerCase();
			const chipsFiltrados: ChipOption[] = this.chips
				.filter(chip => chip.label.toLowerCase().indexOf(filterValue) !== -1) // Coincidencia con lo escrito
				.filter(chip => !this.chipOptionsSelected.find(cos => cos.label === chip.label)); // Que no repita filtro
			return chipsFiltrados;
		} else {
			return [];
		}
	}

	onBlur() {
		if (this.typing) {
			const largo = this.chipOptionsSelected.length;
			this.chipOptionsSelected = this.chipOptionsSelected.filter(cos => cos.value || !!cos.simple);
			if (this.chipOptionsSelected.length !== largo) {
				this.chipsChange.emit(this.chipsValues);
			}
			this.typing = false;
		}
	}

	focus() {
		this.input.nativeElement.focus();
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		if (event.keyCode == ENTER) {
			event.preventDefault();
			event.stopPropagation();
			setTimeout(() => {
				if (!this.focused) {
					// le damos foco para que no se nos vaya por el trfocusnext por si queremos seguir aplicando chips
					this.focus();
				}
			});
		}
	}

}
